/**
 * Structured Data Component
 * Implements Schema.org markup for better SEO
 */

import React, { useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

interface StructuredDataProps {
  type?: 'home' | 'features' | 'pricing';
}

export const StructuredData: React.FC<StructuredDataProps> = React.memo(({ type = 'home' }) => {
  const { language } = useLanguage();

  // Memoize all schemas to prevent recreation on every render
  const mobileAppSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Mirolumo',
    applicationCategory: 'LifestyleApplication',
    applicationSubCategory: 'HealthApplication',
    operatingSystem: 'iOS',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      priceValidUntil: '2026-12-31',
      availability: 'https://schema.org/InStock',
      description: language === 'de' 
        ? 'Kostenloser Download mit Premium-Abo verfügbar' 
        : 'Free download with premium subscription available'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127',
      bestRating: '5',
      worstRating: '1'
    },
    description: language === 'de'
      ? 'Mirolumo ist Ihre tägliche Quelle für Inspiration und Motivation. Mit KI-generierten Bildern, 5 inspirierenden Kategorien (Tägliche Inspiration, Sport & Disziplin, Leadership & Mindset, Achtsamkeit & Balance, Beziehungen & Liebe) und einem persönlichen Journal für Reflexionen.'
      : 'Mirolumo is your daily source of inspiration and motivation. With AI-generated images, 5 inspiring categories (Daily Inspiration, Sports & Discipline, Leadership & Mindset, Mindfulness & Balance, Relationships & Love), and a personal journal for reflections.',
    screenshot: [
      'https://mirolumo.com/screenshots/daily-quotes.png',
      'https://mirolumo.com/screenshots/categories.png',
      'https://mirolumo.com/screenshots/reflection.png',
      'https://mirolumo.com/screenshots/favorites.png',
      'https://mirolumo.com/screenshots/share.png'
    ],
    featureList: language === 'de'
      ? [
          'Tägliche inspirierende Zitate',
          '5 Kategorien für verschiedene Lebensbereiche',
          'KI-generierte Bilder',
          'Persönliches Journaling',
          'Favoriten & Teilen',
          'Dark Mode',
          'Mehrsprachig (DE, EN, ES, FR)'
        ]
      : [
          'Daily inspirational quotes',
          '5 categories for different life areas',
          'AI-generated images',
          'Personal journaling',
          'Favorites & Sharing',
          'Dark Mode',
          'Multilingual (DE, EN, ES, FR)'
        ],
    inLanguage: ['de', 'en', 'es', 'fr'],
    author: {
      '@type': 'Organization',
      name: 'Mirolumo',
      url: 'https://mirolumo.com'
    },
    downloadUrl: 'https://apps.apple.com/us/app/mirolumo-t%C3%A4gliche-inspiration/id6748838555',
    installUrl: 'https://apps.apple.com/us/app/mirolumo-t%C3%A4gliche-inspiration/id6748838555',
    softwareVersion: '1.0.0',
    datePublished: '2025-10-01',
    permissions: language === 'de'
      ? 'Benötigt Berechtigung für Benachrichtigungen (optional)'
      : 'Requires notification permission (optional)'
  }), [language]);

  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mirolumo',
    url: 'https://mirolumo.com',
    logo: 'https://mirolumo.com/logo512.png',
    description: language === 'de'
      ? 'Mirolumo - Deine tägliche Quelle für Inspiration und Motivation'
      : 'Mirolumo - Your daily source of inspiration and motivation',
    sameAs: [
      // Add social media URLs when available
      // 'https://twitter.com/mirolumo',
      // 'https://instagram.com/mirolumo',
      // 'https://facebook.com/mirolumo'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@mirolumo.com',
      availableLanguage: ['German', 'English', 'Spanish', 'French']
    }
  }), [language]);

  const websiteSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Mirolumo',
    url: 'https://mirolumo.com',
    description: language === 'de'
      ? 'Die offizielle Webseite von Mirolumo - Tägliche Inspiration & Motivation'
      : 'The official website of Mirolumo - Daily Inspiration & Motivation',
    inLanguage: ['de', 'en', 'es', 'fr'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://mirolumo.com/?q={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  }), [language]);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'de' ? 'Startseite' : language === 'en' ? 'Home' : language === 'es' ? 'Inicio' : 'Accueil',
        item: `https://mirolumo.com${language === 'de' ? '' : `/${language}`}`
      }
    ]
  }), [language]);

  // Software Application Schema - unused, removed to save memory
  /* const softwareAppSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Mirolumo',
    applicationCategory: 'Lifestyle',
    operatingSystem: 'iOS',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '14.99',
      priceCurrency: 'EUR',
      offerCount: '2',
      offers: [
        {
          '@type': 'Offer',
          name: language === 'de' ? 'Kostenlose Version' : 'Free Version',
          price: '0',
          priceCurrency: 'EUR'
        },
        {
          '@type': 'Offer',
          name: language === 'de' ? 'Premium Jahresabo' : 'Premium Annual Subscription',
          price: '14.99',
          priceCurrency: 'EUR',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '14.99',
            priceCurrency: 'EUR',
            referenceQuantity: {
              '@type': 'QuantityValue',
              value: '1',
              unitCode: 'ANN'
            }
          }
        }
      ]
    }
  }), [language]); */

  return (
    <>
      {/* Mobile Application Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(mobileAppSchema) }}
      />
      
      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      
      {/* WebSite Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      
      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
});

StructuredData.displayName = 'StructuredData';

export default StructuredData;


